import React, { useMemo } from 'react';

import {
  Alert,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type {
  OpportunityRelatedBundledExperience,
  OpportunityRelatedDeal,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import getErrorMessage from '~/utils/getErrorMessage';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import SalesforceLinkButton from '../../shared/SalesforceLinkButton';
import TabPanelShell from '../../shared/TabPanelShell';
import {
  formatDealOptionCurrency,
  formatDealOptionValue,
  normaliseDealOptionText as normalise,
} from '../dealOptions/dealOptionsFormatters';
import type { OpportunityContextFields } from '../dealOptions/dealOptionsModel';

interface Props {
  bundledExperiences: Array<OpportunityRelatedBundledExperience>;
  deals: Array<OpportunityRelatedDeal>;
  error?: unknown;
  isLoading: boolean;
  opportunityContext?: OpportunityContextFields;
}

function getDealOptionName(dealId: string | null | undefined, dealNamesById: Map<string, string>) {
  const key = normalise(dealId);
  return (key && dealNamesById.get(key)) ?? '—';
}

export default function BundledExperiencesTab({
  bundledExperiences,
  deals,
  error,
  isLoading,
  opportunityContext,
}: Props) {
  const dealNamesById = useMemo(() => {
    const namesById = new Map<string, string>();

    deals.forEach((deal) => {
      const dealId = normalise(deal.sfid) ?? normalise(deal.id !== undefined ? String(deal.id) : undefined);
      const dealName = normalise(deal.name);

      if (dealId && dealName) {
        namesById.set(dealId, dealName);
      }
    });

    return namesById;
  }, [deals]);

  useDealInformationTabCount('bundled-experiences', isLoading || error ? undefined : bundledExperiences.length);

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading bundled experiences.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{getErrorMessage(error, 'Unable to load Salesforce bundled experiences.')}</Alert>;
  }

  return (
    <TabPanelShell
      title="Bundled Experiences"
      subtitle={`${bundledExperiences.length} bundled experience${bundledExperiences.length === 1 ? '' : 's'}`}
      contentSx={{ p: 0 }}
    >
      {bundledExperiences.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 3 }} variant="body2">
          No bundled experiences are linked to this opportunity.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Listed In</TableCell>
              <TableCell>Account Name</TableCell>
              <TableCell>Deal Option Name</TableCell>
              <TableCell>Fare Type</TableCell>
              <TableCell>Sub Category</TableCell>
              <TableCell align="right">Ancillary Sell price uplift</TableCell>
              <TableCell align="right">Value (cost currency)</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Destination</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bundledExperiences.map((bundledExperience) => {
              const currency = bundledExperience.currencyisocode ?? '';
              const value = bundledExperience.rrp__c ?? bundledExperience.converted_cost_aud__c ?? null;
              const ancillarySellPriceUplift =
                bundledExperience.sell_price_uplift_number__c ?? bundledExperience.sell_price_uplift__c ?? null;

              return (
                <TableRow key={bundledExperience.id}>
                  <TableCell>
                    <SalesforceLinkButton value={bundledExperience.view_in_led__c} />
                  </TableCell>
                  <TableCell>{formatDealOptionValue(opportunityContext?.accountName)}</TableCell>
                  <TableCell>{getDealOptionName(bundledExperience.deal_option__c, dealNamesById)}</TableCell>
                  <TableCell>
                    {formatDealOptionValue(
                      bundledExperience.fare_type_display__c ?? bundledExperience.channel_fare_type__c,
                    )}
                  </TableCell>
                  <TableCell>{formatDealOptionValue(opportunityContext?.subCategory)}</TableCell>
                  <TableCell align="right">{formatDealOptionValue(ancillarySellPriceUplift)}</TableCell>
                  <TableCell align="right">{formatDealOptionCurrency(currency, value)}</TableCell>
                  <TableCell>{formatDealOptionValue(currency)}</TableCell>
                  <TableCell>{formatDealOptionValue(opportunityContext?.destination)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </TabPanelShell>
  );
}
